import { getTopConnectionLinks } from './getTopConnectionLinks.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

/** Fewer rows than this across the three sources and the byline is hidden. */
export const MIN_BYLINE_MENU_LINKS = 3;
/** The popup never lists more rows than this across its sections; the panel shows the rest. */
export const MAX_BYLINE_MENU_LINKS = 5;

export type BylineMenuLinkSources = {
	relatedContentLinks?: readonly EnhancedSmartLink[];
	incomingLinks?: readonly EnhancedSmartLink[];
	outgoingLinks?: readonly EnhancedSmartLink[];
};

export type BylineMenuLinks = {
	relatedContent: EnhancedSmartLink[];
	appearsIn: EnhancedSmartLink[];
	linksTo: EnhancedSmartLink[];
	/** Rows across the three sections. */
	total: number;
};

/**
 * Picks the rows the byline popup lists: up to `limit` in total, filled in priority order from
 * Related content, then Appears in (incoming), then Links to (outgoing). Related content is
 * preferred because it is the graph's ranked view; the other two are fallbacks for pages where
 * related content is unavailable or thin. Each section is its own statement about the page, so an
 * entity that is both related and a referrer is listed under both. Links to keeps the preview
 * panel's order (unconnected first, then document order).
 */
export const selectBylineMenuLinks = (
	{ relatedContentLinks = [], incomingLinks = [], outgoingLinks = [] }: BylineMenuLinkSources,
	limit: number = MAX_BYLINE_MENU_LINKS,
): BylineMenuLinks => {
	const relatedContent = relatedContentLinks.slice(0, limit);
	const appearsIn = incomingLinks.slice(0, limit - relatedContent.length);
	const linksTo = getTopConnectionLinks(
		[...outgoingLinks],
		limit - relatedContent.length - appearsIn.length,
	);
	return {
		relatedContent,
		appearsIn,
		linksTo,
		total: relatedContent.length + appearsIn.length + linksTo.length,
	};
};

/**
 * Whether the three sources together have enough rows for the byline to show. Counted on the
 * capped selection, which is safe because the cap is above the minimum.
 */
export const hasEnoughBylineMenuLinks = (sources: BylineMenuLinkSources): boolean =>
	selectBylineMenuLinks(sources).total >= MIN_BYLINE_MENU_LINKS;
